import { Controller } from "@hotwired/stimulus"
import { ensureChart } from "./vendor_loader"
import { responseIsCurrent } from "../lib/request_freshness"
import { WEB_EVENTS } from "../lib/web_events"
import { logEvent } from "../lib/client_logger"

// Shared USGS gauge readings modal: latest flow + gage height, parameter/period
// tabs, and a Chart.js trend. Both the main map and the water map open it by
// dispatching the shared usgsSite window event with { site, title }.
export default class extends Controller {
  static targets = [
    "title", "sub", "status",
    "flowValue", "flowTime", "heightValue", "heightTime", "trendArrow",
    "chart", "chartLabel", "tabDischarge", "tabGageHeight", "tabToday", "tab3d", "tab7d"
  ]
  static values = { siteUrl: String }

  connect() {
    this.connected = true
    this.loadRequestId = 0
    this.openHandler = e => this.openSite(e.detail.site, e.detail.title)
    window.addEventListener(WEB_EVENTS.usgsSite, this.openHandler)
    this.escHandler = e => { if (e.key === "Escape") this.close() }
    document.addEventListener("keydown", this.escHandler)
  }

  disconnect() {
    this.connected = false
    this.loadRequestId += 1
    window.removeEventListener(WEB_EVENTS.usgsSite, this.openHandler)
    document.removeEventListener("keydown", this.escHandler)
    if (this.chart) this.chart.destroy()
  }

  async openSite(site, title) {
    const requestId = ++this.loadRequestId
    this.currentSite = site
    this.range = "P7D"      // default to last 7 days
    this.param = "discharge"
    this.titleTarget.textContent = (title || "").replace(/^🌊\s*/, "")
    this.subTarget.textContent = `USGS gauge ${site}`
    this.element.classList.remove("hidden")
    this.element.classList.add("flex")
    this.#updateRangeTabs()
    try {
      await ensureChart()
    } catch (error) {
      if (!this.#loadCurrent(requestId)) return
      logEvent("usgs_modal.chart_initialize_failed", error)
      this.statusTarget.textContent = "Chart failed to load. Check your connection and try again."
      return
    }
    if (this.#loadCurrent(requestId)) this.#load(requestId)
  }

  close() {
    this.loadRequestId += 1
    this.element.classList.add("hidden")
    this.element.classList.remove("flex")
    if (this.chart) { this.chart.destroy(); this.chart = null }
  }

  // Fetch the current site at the current range and render readings + chart.
  async #load(requestId = ++this.loadRequestId) {
    this.#resetReadings()
    this.statusTarget.textContent = "Loading…"
    const site = this.currentSite
    const range = this.range
    try {
      const res = await fetch(
        `${this.siteUrlValue}?site=${encodeURIComponent(site)}&period=${range}`,
        { headers: { Accept: "application/json" } })
      const data = await res.json()
      if (!this.#loadCurrent(requestId)) return
      if (!data.ok) throw new Error(data.error || data.message || "No data")

      this.site = data
      this.#renderReadings(data)
      if (!data[this.param]) this.param = data.discharge ? "discharge" : "gageHeight"
      this.#showParam(this.param)
      this.statusTarget.textContent = (data.discharge || data.gageHeight) ? "" : "No recent readings"
    } catch (e) {
      if (this.#loadCurrent(requestId)) this.statusTarget.textContent = `Error: ${e.message}`
    }
  }

  #loadCurrent(requestId) {
    return responseIsCurrent({ connected: this.connected, requestId, latestRequestId: this.loadRequestId })
  }

  showDischarge() { this.param = "discharge"; this.#showParam("discharge") }
  showGageHeight() { this.param = "gageHeight"; this.#showParam("gageHeight") }
  showToday()  { this.#setRange("P1D") }
  show3Days()  { this.#setRange("P3D") }
  show7Days()  { this.#setRange("P7D") }

  #setRange(period) {
    if (this.range === period) return
    this.range = period
    this.#updateRangeTabs()
    this.#load()
  }

  #updateRangeTabs() {
    for (const [tab, period] of [[this.tabTodayTarget, "P1D"], [this.tab3dTarget, "P3D"], [this.tab7dTarget, "P7D"]]) {
      const on = period === this.range
      tab.classList.toggle("bg-sky-600", on)
      tab.classList.toggle("text-white", on)
      tab.classList.toggle("text-slate-300", !on)
    }
  }

  #resetReadings() {
    for (const t of [this.flowValueTarget, this.heightValueTarget]) t.textContent = "–"
    for (const t of [this.flowTimeTarget, this.heightTimeTarget]) t.textContent = ""
    this.trendArrowTarget.textContent = ""
  }

  #renderReadings(data) {
    if (data.discharge?.latest) {
      this.flowValueTarget.textContent = fmt(data.discharge.latest.v)
      this.flowTimeTarget.textContent = fmtReadingTime(data.discharge.latest.t)
    }
    if (data.gageHeight?.latest) {
      this.heightValueTarget.textContent = fmt(data.gageHeight.latest.v)
      this.heightTimeTarget.textContent = fmtReadingTime(data.gageHeight.latest.t)
    }

    // Trend arrow from the last few discharge points (rising / steady / falling).
    const s = data.discharge?.series
    if (s && s.length > 4) {
      const delta = s[s.length - 1].v - s[s.length - 5].v
      const pct = delta / (s[s.length - 5].v || 1)
      this.trendArrowTarget.textContent = pct > 0.02 ? "↗" : pct < -0.02 ? "↘" : "→"
    }
  }

  // Render the chart for one parameter and highlight its tab.
  #showParam(which) {
    const param = this.site?.[which]
    this.chartLabelTarget.textContent = which === "discharge" ? "Streamflow" : "Gage height"

    for (const [tab, key] of [[this.tabDischargeTarget, "discharge"], [this.tabGageHeightTarget, "gageHeight"]]) {
      const on = key === which
      tab.classList.toggle("bg-sky-600", on)
      tab.classList.toggle("text-white", on)
      tab.classList.toggle("text-slate-300", !on)
    }
    if (!param?.series?.length) return

    const times = param.series.map(p => p.t)
    const values = param.series.map(p => p.v)
    const unit = which === "discharge" ? "ft³/s" : "ft"
    const today = this.range === "P1D"
    const axisLabel = iso => today ? fmtClock(iso) : fmtDayOrdinal(iso)

    if (this.chart) this.chart.destroy()
    this.chart = new window.Chart(this.chartTarget, {
      type: "line",
      data: {
        labels: times,
        datasets: [{
          data: values,
          borderColor: "#7dd3fc",
          backgroundColor: "rgba(56,189,248,0.15)",
          fill: true, borderWidth: 2, pointRadius: 0, tension: 0.3
        }]
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: { callbacks: {
            title: c => fmtReadingTime(times[c[0].dataIndex]),
            label: c => `${fmt(c.parsed.y)} ${unit}`
          } }
        },
        scales: {
          x: {
            grid: { display: false },
            afterBuildTicks: (scale) => {
              const n = times.length
              const want = Math.min(n, today ? 5 : 7)
              const denom = Math.max(1, want - 1)
              const idx = []
              for (let i = 0; i < want; i++) idx.push(Math.round(i * (n - 1) / denom))
              scale.ticks = [...new Set(idx)].map(value => ({ value }))
            },
            ticks: {
              color: "#94a3b8", maxRotation: 0, autoSkip: false,
              callback: (val) => axisLabel(times[val])
            }
          },
          y: { ticks: { color: "#94a3b8" }, grid: { color: "rgba(148,163,184,0.15)" } }
        }
      }
    })
  }
}

function fmt(n) {
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })
}

// Clock time in the browser's timezone, e.g. "2:00 PM".
function fmtClock(iso) {
  const d = new Date(iso)
  return isNaN(d) ? "" : d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
}

// Ordinal day of month, e.g. "31st", "1st", "2nd".
function fmtDayOrdinal(iso) {
  const d = new Date(iso)
  if (isNaN(d)) return ""
  const n = d.getDate()
  const s = ["th", "st", "nd", "rd"], v = n % 100
  return n + (s[(v - 20) % 10] || s[v] || s[0])
}

// "Today 2:00 PM" if today, otherwise "Jun 1, 2:00 PM" — browser timezone.
function fmtReadingTime(iso) {
  const d = new Date(iso)
  if (isNaN(d)) return ""
  const clock = fmtClock(iso)
  const isToday = d.toDateString() === new Date().toDateString()
  return isToday ? `Today ${clock}`
    : `${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}, ${clock}`
}
